<template>
  <v-container fluid>
    <v-data-table
      :headers="headers"
      :items="diagnostics"
      :loading="loading"
      :items-per-page="itemsPerPage"
      :page.sync="currentPage"
      class="elevation-1"
      hide-default-footer
    >
      <template v-slot:item.id="{ item }">
        <span class="font-weight-bold">{{ item.id }}</span>
      </template>

      <template v-slot:item.nombre="{ item }">
        <span>{{ item.nombre }}</span>
      </template>

      <template v-slot:item.grado="{ item }">
        <v-chip small>{{ item.grado }}°</v-chip>
      </template>

      <template v-slot:item.nivel="{ item }">
        <v-chip
          small
          :color="getLevelColor(item.nivel)"
          text-color="white"
        >
          {{ $t(`diagnostics.levels.${item.nivel}`) }}
        </v-chip>
      </template>

      <template
        v-for="country in supportedCountries"
        v-slot:[`item.variant${country.code}`]="{ item }"
      >
        <v-chip
          :key="country.code"
          small
          :color="item[`variant${country.code}`] ? 'green' : 'grey lighten-2'"
          :text-color="item[`variant${country.code}`] ? 'white' : 'grey darken-2'"
        >
          {{ item[`variant${country.code}`] ? '✓' : '−' }}
        </v-chip>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          small
          color="deep-purple lighten-1"
          text
          @click.stop="handleEdit(item)"
          class="cursor-pointer"
        >
          <v-icon small class="mr-1">mdi-pencil-outline</v-icon>
          {{ $t('diagnostics.edit') }}
        </v-btn>
      </template>

      <template v-slot:no-data>
        <div class="text-center pa-4">
          {{ $t('diagnostics.no_diagnostics_found') }}
        </div>
      </template>
    </v-data-table>

    <div class="text-center pt-4">
      <v-pagination
        v-model="currentPage"
        :length="totalPages"
        :total-visible="7"
        circle
        @input="handlePageChange"
      />
    </div>
  </v-container>
</template>

<script>
import { SUPPORTED_COUNTRIES } from '@/constants/diagnostics';

export default {
  name: 'DiagnosticsDataTable',
  props: {
    loading: { type: Boolean, default: false },
    diagnostics: { type: Array, default: () => [] },
    selectedCountry: { type: String, default: 'GENERAL' },
    value: { type: Number, default: 1 },
    length: { type: Number, default: 1 },
  },
  data() {
    return {
      itemsPerPage: 15,
      currentPage: this.value,
      supportedCountries: SUPPORTED_COUNTRIES,
    };
  },
  computed: {
    countryColumns() {
      return SUPPORTED_COUNTRIES.map((country) => ({
        text: `${country.code} ${country.flag}`,
        value: `variant${country.code}`,
        align: 'center',
        sortable: false,
        width: '10%',
      }));
    },
    headers() {
      return [
        {
          text: 'ID',
          value: 'id',
          align: 'center',
          sortable: true,
          width: '8%',
        },
        {
          text: this.$t('diagnostics.name'),
          value: 'nombre',
          align: 'start',
          sortable: true,
          width: '35%',
        },
        {
          text: this.$t('diagnostics.grade'),
          value: 'grado',
          align: 'center',
          sortable: true,
          width: '10%',
        },
        {
          text: this.$t('diagnostics.level'),
          value: 'nivel',
          align: 'center',
          sortable: true,
          width: '12%',
        },
        ...this.countryColumns,
        {
          text: this.$t('diagnostics.actions'),
          value: 'actions',
          align: 'center',
          sortable: false,
          width: '15%',
        },
      ];
    },
    totalPages() {
      return this.length;
    },
  },
  watch: {
    value(newValue) {
      this.currentPage = newValue;
    },
  },
  methods: {
    getLevelColor(level) {
      const colors = {
        INITIAL: 'blue',
        INTERMEDIATE: 'orange',
        ADVANCED: 'red',
      };
      return colors[level] || 'grey';
    },
    handleEdit(item) {
      this.$router.push({
        path: `/dashboard/diagnostics/${item.id}/questions`,
        query: { country: this.selectedCountry },
      });
    },
    handlePageChange(page) {
      this.$emit('input', page);
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.v-data-table >>> tbody tr {
  cursor: default;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}
</style>
